import type { Door, Zone } from '@/content/schema';
import type { Ctx } from '@/core/context';
import { bus } from '@/core/bus';
import { nextRandom } from '@/core/rng';
import { bump, setFlag } from './achievements';
import { hasItem } from './items';
import { NEWBIE_DAYS } from '@/core/state';
import { gainXp } from './progress';
import { whoSees } from './watchers';
import { eligible, fire } from './events';
import { tr } from '@/i18n';

/**
 * 衛生動線（v0.4）
 * 非生產區(-1) ─更衣→ 氣浴室（雙向，進場方向才吹風）→ 洗手區(0)：洗手、酒精、烘手 ─推門→ 生產區(1/2)
 * - 進生產區的門有 gate:'wash'（v0.5）：沒洗手「推得開」，但會跳出警示、記一次衛生缺失（被品保／駐廠看到會扣信任）
 *   v1.0：穿了工作服、沒走氣浴室（從進貨區、冰箱繞進來）也推得開，但算違規（noAirshowerEntry）
 * - 回到非生產區（或走出氣浴室）→ 洗手失效，下次進來要重洗
 * - 生產區裡往更乾淨的區域：單向門、出口，逆向推不開；請同事偷開＝違規（SHORT-006）
 * - 員工大門 gate:'clock'：先在廠區外的打卡鐘打卡
 * - 上鎖的門要鑰匙；暗門要先找到；冷凍冷藏庫、碼頭拉門要按 E 推／拉開（manual，物理上關著）
 */
export const LEVEL_NAME: Record<number, string> = { [-1]: tr("非生產區"), 0: tr("一般作業區"), 1: tr("準清潔區"), 2: tr("清潔區") };

export function doorBetween(ctx: Ctx, a: string, b: string, x: number, z: number): Door | null {
  let best: Door | null = null, bd = 2.4;
  for (const { data: d } of ctx.world.rt().doors.values()) {
    if (!((d.from === a && d.to === b) || (d.from === b && d.to === a))) continue;
    const dist = Math.hypot(d.pos[0] - x, d.pos[1] - z);
    if (dist < bd) { bd = dist; best = d; }
  }
  return best;
}

export type CrossResult = { ok: true; door: Door | null; reverse: boolean; unwashed?: boolean; noAirshower?: boolean } | { ok: false; door: Door | null; reason: string };

/** 每次玩家要跨到別的區域前呼叫（Game.blocked） */
export function canCross(ctx: Ctx, from: Zone, to: Zone, x: number, z: number): CrossResult {
  const door = doorBetween(ctx, from.id, to.id, x, z);
  if (!door) return from.level === to.level ? { ok: true, door: null, reverse: false } : { ok: false, door: null, reason: tr("這裡過不去。") };
  const forward = door.from === from.id;
  if (forward && door.gate === 'clock' && !ctx.day.clockedIn) return { ok: false, door, reason: tr("⏰ 先在大門旁邊的打卡鐘打卡，才能進廠。") };
  // v1.0：穿了工作服、沒過氣浴室——推得開，但算違規（跟沒洗手一樣：會跳警示，被看到要記缺失）
  if (forward && door.gate === 'wash' && !ctx.day.inProd) {
    if (!ctx.day.dressed) return { ok: false, door, reason: tr("🥼 要先換工作服、過氣浴室、洗手，才能進生產區。") };
    return { ok: true, door, reverse: false, noAirshower: true, unwashed: !ctx.day.washed };
  }
  if (forward && door.gate === 'wash' && !ctx.day.washed) return { ok: true, door, reverse: false, unwashed: true };
  switch (door.type) {
    case 'open': return { ok: true, door, reverse: false };
    case 'hidden': return ctx.day.flags[door.flag ?? ''] ? { ok: true, door, reverse: false } : { ok: false, door, reason: '' };
    case 'locked': return hasItem(ctx, door.key ?? '') ? { ok: true, door, reverse: false } : { ok: false, door, reason: tr("🔒 {0}。鑰匙……問問倉儲課長？", door.name) };
    case 'airshower':
      if (!forward) return { ok: true, door, reverse: false };      // 出來的方向直接通過，不吹風、不算違規
      if (!ctx.day.dressed) return { ok: false, door, reason: tr("🥼 要先到更衣室換上工作服、戴口罩和髮帽、穿雨鞋，才能進氣浴室。") };
      return { ok: true, door, reverse: false };
    default: // oneway / exit
      if (forward) return { ok: true, door, reverse: false };
      return ctx.day.flags[`permit:${door.id}`] ? { ok: true, door, reverse: true } : { ok: false, door, reason: '' };
  }
}

/** 被單向門擋住時：提醒＋「請同事偷開門」選項（SHORT-006） */
export function blockedAtDoor(ctx: Ctx, r: Extract<CrossResult, { ok: false }>): void {
  const d = r.door;
  if (d && !r.reason && (d.type === 'oneway' || d.type === 'exit') && !ctx.ui.isOpen()) {
    const ev = ctx.c.eventById.get('SHORT-006');
    const key = `asked:${d.id}`;
    if (ev && !ctx.day.flags[key]) {
      ctx.day.flags[key] = true; setTimeout(() => (ctx.day.flags[key] = false), 8000);
      fire(ctx, ev, safe => {
        if (safe) { ctx.world.pushBack(d.pos, 1.2); ctx.ui.toast(tr("照規定：走出生產區 → 氣浴室 → 洗手，再從正確的門進來。")); }
        else { ctx.day.flags[`permit:${d.id}`] = true; ctx.ui.toast(tr("同事幫你從裡面把門推開了……快過去。")); setTimeout(() => (ctx.day.flags[`permit:${d.id}`] = false), 6000); }
      }, { ignoreGate: true });
      return;
    }
  }
  if (r.reason) ctx.ui.toast(r.reason);
}

export function onZoneChange(ctx: Ctx, from: Zone | null, to: Zone | null, cross: Extract<CrossResult, { ok: true }> | null): void {
  if (!from || !to || from.id === to.id) return;
  const day = ctx.day;
  if (!day.floorsVisited.includes(day.floor)) day.floorsVisited.push(day.floor);
  if (to.cold && !from.cold) { bump(ctx, 'enteredCold'); ctx.ui.toast(to.cold === 'frozen' ? tr("🧊 好冷！冷凍庫裡不要待太久。") : tr("❄️ 進冷藏冰箱。")); }
  if (to.level < 0) { day.washed = false; day.washStep = 0; day.inProd = false; }   // 回到非生產區：手要重洗、要重新過氣浴室
  if (to.outdoor && !from.outdoor) ctx.ui.toast(tr("🌤️ 走到廠區室外。圍籬就是邊界。"));
  const door = cross?.door ?? null;
  if (cross?.reverse && door) return violation(ctx, door, from, to);
  if (!door) return;
  if (cross?.noAirshower) return noAirshowerEntry(ctx, door, to, !!cross.unwashed);
  if (cross?.unwashed) return unwashedEntry(ctx, door, to);
  if (door.type === 'airshower') {
    if (door.from === from.id) {
      const H = ctx.c.game.hygiene;
      ctx.advance(H.airshowerMinutes, tr("氣浴")); bump(ctx, 'airshower'); bus.emit('sfx', 'door');
      ctx.world.airshower(H.airshowerSeconds);
      day.washed = false; day.washStep = 0; day.inProd = true;
      ctx.ui.toast(tr("💨 氣浴中：張開手轉一圈。出去先洗手！"));
    } else { day.washed = false; day.inProd = false; ctx.ui.toast(tr("🚪 從氣浴室出來（離場方向不吹風）。回生產區要重新走一次。")); }
  } else if (door.gate === 'wash' && door.from === from.id) ctx.ui.toast(tr("🚪 推開門，進入{0}（{1}）。", to.name, LEVEL_NAME[to.level]));
  else if (door.type === 'oneway') ctx.ui.toast(tr("🚪 {0}：{1} → {2}。這扇門回不去了。", door.name, LEVEL_NAME[from.level], LEVEL_NAME[to.level]));
  else if (door.type === 'exit') ctx.ui.toast(tr("🚪 走出{0}（{1}）。要回去得重新走氣浴室、洗手。", from.name, to.name));
  else if (door.type === 'locked') { setFlag(ctx, 'openedChem'); ctx.ui.toast(tr("🗝️ 打開化學品儲藏室。用完記得登記。")); }
  else if (door.gate === 'clock' && door.from === from.id) ctx.ui.toast(tr("🏭 進廠。生產區：換裝 → 氣浴室 → 洗手。"));
}

/** v1.1.1 洗手要走三台機器：洗手台（洗手）→ 酒精消毒機 → 烘手機。每台一題簡單問答；答錯給提示再選一次 */
export type WashKind = 'sink' | 'sprayer' | 'dryer';
export const WASH_ORDER: WashKind[] = ['sink', 'sprayer', 'dryer'];
export const washKindOf = (eqKind: string): WashKind => eqKind === 'sprayer' ? 'sprayer' : eqKind === 'dryer' ? 'dryer' : 'sink';
const washName = (k: WashKind) => k === 'sink' ? tr("🧼 洗手台") : k === 'sprayer' ? tr("🧴 酒精消毒機") : tr("💨 烘手機");
/** 純函式：在這台機器做洗手的下一步會怎樣 */
export function washNext(step: number, k: WashKind): 'ok' | 'early' | 'done' {
  const i = WASH_ORDER.indexOf(k);
  return i < step ? 'done' : i === step ? 'ok' : 'early';
}
export function washCard(ctx: Ctx, kind: WashKind, then?: () => void): void {
  const day = ctx.day;
  const step = day.washed ? 3 : (day.washStep ?? 0);
  const order = WASH_ORDER.map((k, j) => `${j < step ? '✔' : j === step ? '▶' : '・'} ${washName(k)}`).join('　');
  const r = washNext(step, kind);
  if (r !== 'ok') {
    const msg = step >= 3 ? tr("✨ 手已經洗好了。") : r === 'early' ? tr("先去 {0}。", washName(WASH_ORDER[step])) : tr("這步做過了，下一步：{0}", washName(WASH_ORDER[step]));
    ctx.ui.toast(`${msg}（${order}）`); return;
  }
  // v1.1.2 每台一個按鈕（不考問），文字照原本的說明
  const S: Record<WashKind, { icon: string; name: string; text: string }> = {
    sink: { icon: '🧼', name: tr("洗手"), text: tr("打濕、抹肥皂、搓 30 秒（手心、手背、指縫、指尖、大拇指、手腕），沖乾淨。") },
    sprayer: { icon: '🧴', name: tr("酒精消毒"), text: tr("手伸到酒精機下面，噴一下，兩手搓均勻。") },
    dryer: { icon: '💨', name: tr("烘手機"), text: tr("烘乾。濕的手最容易沾細菌。") },
  };
  const q = S[kind]; const i = WASH_ORDER.indexOf(kind);
  const list = WASH_ORDER.map((k, j) => `${j < i ? '✔' : j === i ? '▶' : tr("・")} ${S[k].icon} ${S[k].name}`);
  ctx.ui.card({ name: tr("洗手區"), role: tr("第 {0}／3 步", i + 1), color: '#5aa9e6', title: `${q.icon} ${q.name}`, text: q.text,
    sections: [{ label: tr("順序"), items: list }],
    choices: [{ text: `${q.icon} ${q.name}`, tone: 'primary', fn: () => {
      bus.emit('sfx', 'click');
      if (i < 2) {
        day.washStep = i + 1; ctx.ui.close();
        ctx.ui.toast(tr("✔ {0}　下一步 👉 {1}", q.name, washName(WASH_ORDER[i + 1]))); ctx.save(); return;
      }
      ctx.advance(ctx.c.game.hygiene.washMinutes, tr("洗手")); bump(ctx, 'handwash'); gainXp(ctx, 'wash');
      day.washed = true; day.washStep = 0; ctx.ui.close();
      ctx.ui.toast(tr("✨ 洗好了！可以推門進生產區。")); then?.(); ctx.save();
    } }] });
}

/** 手套・圍裙補給架 */
export function supplyCard(ctx: Ctx): void {
  ctx.ui.card({ name: tr("手套・圍裙補給架"), role: tr("洗手區"), color: '#4f86b8',
    text: tr("換產品就要換手套、圍裙。"), foldSections: true, more: tr("拋棄式圍裙用完就丟；可重複使用的圍裙每次換產品用水沖洗。"),
    sections: [{ label: tr("圍裙分色"), items: [tr("生食人員：拋棄式圍裙＋藍色手套"), tr("熟食人員、煮飯區：天藍色可重複使用圍裙＋淺藍色手套"), tr("前置區、1F 洗籃區、2F 洗籃區：灰黑色可重複使用圍裙"), tr("油炸機前後的兩個人不能互相接觸、不能交換工具")], tone: 'info' }],
    choices: [{ text: tr("戴好手套、穿上圍裙"), tone: 'primary', fn: () => { bump(ctx, 'gloved'); ctx.ui.close(); ctx.ui.toast(tr("🧤 準備好了。")); } }] });
}

function violation(ctx: Ctx, door: Door, from: Zone, to: Zone): void {
  const { day, c } = ctx; const H = c.game.hygiene;
  const watcher = whoSees(ctx);
  const supervisor = !watcher && nextRandom(day) < c.game.watchers.supervisorNoticeChance;
  if (!ctx.profile.hazardsSeen.includes('H018')) ctx.profile.hazardsSeen.push('H018');
  day.flags[`permit:${door.id}`] = false;
  if (watcher || supervisor) {
    day.factory.caught++; day.factory.fines += H.finePerViolation; bump(ctx, 'caught');
    const external = watcher?.id === 'resident';
    ctx.apply({ audit: -(external ? H.auditPenaltyCaught : Math.round(H.auditPenaltyCaught / 2)), trust: -H.trustPenaltyCaught, foodSafety: -2 }, 'hygiene');
    bus.emit('sfx', 'warn');
    ctx.log(tr("逆行（{0} → {1}）被{2}看到，罰款 ${3}", from.name, to.name, watcher ? watcher.name : tr("主管"), H.finePerViolation), 'bad');
    const line = watcher?.lines?.caughtReverse;
    ctx.ui.card({ name: watcher ? watcher.name : tr("值班主管"), role: watcher ? watcher.title : tr("生產部"), color: watcher ? watcher.color : '#c98f6b', npc: watcher?.id,
      badge: { text: tr("逆行｜罰款 ${0}", H.finePerViolation), color: '#d9534a' },
      text: (Array.isArray(line) ? line[0] : line) ?? tr("欸！那扇是單向門，你怎麼從這邊過來的？罰款一百，下次照動線繞。"),
      sections: [{ label: tr("廠規"), items: [c.rules.flowRule] }, ...(external ? [{ label: tr("注意"), items: [tr("駐廠人員是超商派來的外部人員，她的紀錄會影響訂單。")], tone: 'warn' as const }] : [])],
      choices: [{ text: tr("是，下次照動線走：出去 → 氣浴室 → 洗手"), tone: 'primary', fn: () => ctx.ui.close() }] });
  } else {
    day.factory.unseen++;
    ctx.apply({ foodSafety: -2 }, 'hygiene');
    ctx.log(tr("逆行（{0} → {1}），沒人看到", from.name, to.name), 'warn');
    ctx.ui.toast(tr("👀 沒人看到……但其實是不行的！（交叉污染風險已記錄）"));
  }
  ctx.save();
}

/** v0.5：沒洗手就進作業區——可以進去，但一定會跳警示；被巡場的人看到會被唸、扣信任 */
function unwashedEntry(ctx: Ctx, door: Door, to: Zone): void {
  const { day } = ctx;
  day.flags.unwashedToday = true; bump(ctx, 'unwashed');
  if (!ctx.profile.hazardsSeen.includes('H018')) ctx.profile.hazardsSeen.push('H018');
  const watcher = whoSees(ctx);
  bus.emit('sfx', 'warn');
  if (watcher) {
    day.factory.caught++;
    ctx.apply({ foodSafety: -3, trust: -3, audit: watcher.id === 'resident' ? -4 : -1 }, 'hygiene');
    ctx.log(tr("沒洗手就進{0}，被{1}看到", to.name, watcher.name), 'bad');
    ctx.ui.card({ name: watcher.name, role: watcher.title, color: watcher.color, npc: watcher.id, badge: { text: tr("⚠️ 沒洗手"), color: '#d9534a' }, expression: 'angry',
      text: tr("等一下！你剛剛沒有洗手吧？手上的細菌會直接沾到食材上。回去洗手：洗手 → 酒精 → 烘乾。"),
      choices: [{ text: tr("對不起，我馬上回去洗手"), tone: 'primary', fn: () => { ctx.ui.close(); ctx.world.pushBack(door.pos, 1.4); } }] });
  } else {
    day.factory.unseen++;
    ctx.apply({ foodSafety: -3 }, 'hygiene');
    ctx.log(tr("沒洗手就進{0}（沒人看到）", to.name), 'warn');
    ctx.ui.toast(tr("⚠️ 警示：你沒有洗手就進作業區了！手上的細菌會沾到食物上。回洗手區：洗手 → 酒精 → 烘手機。"));
  }
  ctx.world.refreshActors(); ctx.save();
}

/** v1.0：穿著工作服、沒過氣浴室就進生產區——可以進，但算違規（衣服上的灰塵、毛髮會帶進去） */
function noAirshowerEntry(ctx: Ctx, door: Door, to: Zone, unwashed: boolean): void {
  const { day } = ctx;
  day.inProd = true; day.flags.noAirshowerToday = true; bump(ctx, 'noAirshower');
  if (unwashed) { day.flags.unwashedToday = true; bump(ctx, 'unwashed'); }
  if (!ctx.profile.hazardsSeen.includes('H018')) ctx.profile.hazardsSeen.push('H018');
  const watcher = whoSees(ctx);
  bus.emit('sfx', 'warn');
  const what = unwashed ? tr("沒過氣浴室、也沒洗手") : tr("沒過氣浴室");
  if (watcher) {
    day.factory.caught++; day.factory.fines += ctx.c.game.hygiene.finePerViolation; bump(ctx, 'caught');
    ctx.apply({ foodSafety: unwashed ? -5 : -3, trust: -4, audit: watcher.id === 'resident' ? -5 : -2 }, 'hygiene');
    ctx.log(tr("{0}就進{1}，被{2}看到（違規）", what, to.name, watcher.name), 'bad');
    ctx.ui.card({ name: watcher.name, role: watcher.title, color: watcher.color, npc: watcher.id, badge: { text: tr("⚠️ 違規：{0}", what), color: '#d9534a' }, expression: 'angry',
      text: tr("你從哪裡進來的？衣服上的灰塵、頭髮都還沒吹掉！回更衣室那邊重新走：氣浴室 → 洗手 → 再進來。這次要記一筆。"),
      sections: [{ label: tr("廠規"), items: [ctx.c.rules.flowRule] }],
      choices: [{ text: tr("對不起，我回去重新走一次"), tone: 'primary', fn: () => { ctx.ui.close(); ctx.world.pushBack(door.pos, 1.4); } }] });
  } else {
    day.factory.unseen++;
    ctx.apply({ foodSafety: unwashed ? -5 : -3 }, 'hygiene');
    ctx.log(tr("{0}就進{1}（沒人看到，但算違規）", what, to.name), 'warn');
    ctx.ui.toast(tr("⚠️ 違規：你{0}就進生產區了！衣服上的灰塵、毛髮會帶進去。可以進，但被品保、主管看到會記缺失。", what));
  }
  ctx.world.refreshActors(); ctx.save();
}

/** 更衣室 */
export function lockerCard(ctx: Ctx): void {
  const role = ctx.c.roleById.get(ctx.day.roleId)!;
  const U = { white: tr("全白（生產部：衣服・褲子・帽子都白色）"), gray: tr("全灰（工務部：衣服・褲子・帽子都灰色）"), green: tr("全草綠（倉儲部：衣服・褲子・帽子都草綠色）"), office: tr("全白（行政人員進現場用）"), visitor: tr("訪客服"), street: tr("便服") } as const;
  const hat = role.hat === 'none' && ctx.profile.day <= NEWBIE_DAYS ? 'yellow' : role.hat;
  const hatTxt = ctx.c.rules.hats.find(h => h.hat === hat)?.label ?? tr("一般髮帽");
  if (ctx.day.dressed) {
    ctx.ui.card({ name: tr("更衣室"), role: tr("置物櫃"), color: '#7c8f9c', text: tr("你已經穿好工作服了。要換回便服嗎？（下班或去辦公室時可以換）"),
      choices: [{ text: tr("換回便服"), fn: () => { ctx.day.dressed = false; ctx.day.washed = false; ctx.day.washStep = 0; ctx.advance(2, tr("更衣")); ctx.ui.close(); ctx.world.refreshActors(); ctx.save(); } }, { text: tr("不用"), tone: 'primary', fn: () => ctx.ui.close() }] });
    return;
  }
  ctx.ui.card({ name: tr("更衣室"), role: tr("置物櫃"), color: '#7c8f9c',
    text: tr("換上 {0} 工作服、口罩、髮帽（{1}）、雨鞋。", U[role.uniform], hatTxt), foldSections: true, more: tr("飾品、手錶都放進置物櫃。"),
    sections: [{ label: tr("制服識別"), items: ctx.c.rules.uniforms.map(u => tr("{0}：{1}", u.uniform, u.dept)) }],
    choices: [
      { text: tr("換裝，對著鏡子確認髮帽沒有露出頭髮"), tone: 'primary', fn: () => dress(ctx, true) },
      { text: tr("換裝（趕時間，不照鏡子了）"), fn: () => dress(ctx, false) },
    ] });
}
function dress(ctx: Ctx, careful: boolean) {
  ctx.day.dressed = true; ctx.advance(ctx.c.game.hygiene.dressMinutes, tr("更衣")); bump(ctx, 'dressed'); bus.emit('sfx', 'ok');
  ctx.ui.close(); ctx.world.refreshActors();
  if (!careful) {
    ctx.day.flags.hairOut = true;
    const ev = ctx.c.eventById.get('INT-014'); if (ev && eligible(ctx, ev)) ctx.day.events.last[ev.id] = -9999;
    ctx.ui.toast(tr("有一小撮頭髮露在髮帽外面……你沒發現。"));
  } else ctx.ui.toast(tr("🥼 換好了。去氣浴室吧（出來先洗手）。"));
  ctx.save();
}
